import { Sun, Check, Footprints, UtensilsCrossed, Bike } from "lucide-react";
import { FadeUp, SectionHeading } from "./Reveal";
import { useLang } from "../i18n/LanguageContext";
import { ATTRACTION_IMAGES } from "../data/site";

const INFO_ICONS = [Footprints, UtensilsCrossed, Bike];

export default function Tourism() {
  const { T } = useLang();
  const [thermal, aquatic, lake, ...infoCards] = T.tourism.cards;

  return (
    <section id="federacion" data-testid="tourism-section" className="py-14 md:py-24">
      <div className="container-x">
        <SectionHeading
          eyebrow={T.tourism.eyebrow}
          title={T.tourism.title}
          subtitle={T.tourism.subtitle}
        />

        <div className="mt-12 grid gap-4 sm:gap-5 md:grid-cols-2 lg:grid-cols-3">
          {/* Parque Termal — tall card */}
          <FadeUp className="lg:row-span-2">
            <article
              data-testid="attraction-card-thermal"
              className="zoom-media group relative h-full min-h-[300px] overflow-hidden rounded-3xl shadow-[0_18px_50px_-20px_rgba(64,49,47,0.35)] sm:min-h-[420px] lg:min-h-[640px]"
            >
              <img
                src={ATTRACTION_IMAGES[0]}
                alt={thermal.t}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2b1e1c]/85 via-[#2b1e1c]/25 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-5 sm:p-7">
                <h3 className="font-serif text-2xl font-medium text-federasol-cream sm:text-3xl">
                  {thermal.t}
                </h3>
                <p className="mt-2 text-xs leading-relaxed text-federasol-cream/85 sm:mt-3 sm:text-sm">{thermal.d}</p>
              </div>
            </article>
          </FadeUp>

          {/* Thermal benefits — wide highlight card */}
          <FadeUp delay={0.08} className="md:col-span-2">
            <article
              data-testid="attraction-card-benefits"
              className="flex h-full flex-col justify-between rounded-3xl border border-federasol-rose/25 bg-federasol-cream p-5 shadow-[0_14px_40px_-20px_rgba(166,93,71,0.35)] sm:p-8 md:flex-row md:items-center md:gap-8"
            >
              <div className="md:max-w-xs">
                <p className="eyebrow flex items-center gap-2">
                  <Sun className="h-4 w-4" />
                  {T.tourism.benefits.eyebrow}
                </p>
                <h3 className="mt-3 font-serif text-xl font-semibold text-federasol-cocoa sm:text-3xl">
                  {T.tourism.benefits.title}
                </h3>
                <p className="mt-2 text-xs leading-relaxed text-federasol-muted sm:mt-3 sm:text-sm">
                  {T.tourism.benefits.text}
                </p>
              </div>
              <ul className="mt-4 grid flex-1 gap-2 sm:grid-cols-2 sm:gap-2.5 md:mt-0">
                {T.tourism.benefits.items.map((b) => (
                  <li key={b} className="flex items-center gap-2 rounded-xl bg-federasol-blush/80 px-3 py-2.5 text-xs font-medium text-federasol-cocoa sm:px-4 sm:py-3 sm:text-sm">
                    <Check className="h-3.5 w-3.5 shrink-0 text-federasol-terra sm:h-4 sm:w-4" strokeWidth={2.4} />
                    {b}
                  </li>
                ))}
              </ul>
            </article>
            <p className="mt-2 px-2 text-[11px] italic text-federasol-muted sm:text-xs">
              {T.tourism.benefits.disclaimer}
            </p>
          </FadeUp>

          {/* Photo cards with real images */}
          {[aquatic, lake].map((a, i) => (
            <FadeUp key={a.t} delay={0.05 * (i + 1)}>
              <article
                data-testid={`attraction-card-${i}`}
                className="zoom-media group relative h-full min-h-[240px] overflow-hidden rounded-3xl shadow-[0_14px_40px_-18px_rgba(64,49,47,0.3)] sm:min-h-[300px]"
              >
                <img
                  src={ATTRACTION_IMAGES[i + 1]}
                  alt={a.t}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2b1e1c]/85 via-[#2b1e1c]/25 to-transparent transition-opacity duration-500" />
                <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6">
                  <h3 className="font-serif text-xl font-medium text-federasol-cream sm:text-2xl">{a.t}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-federasol-cream/85 sm:text-sm">{a.d}</p>
                </div>
              </article>
            </FadeUp>
          ))}

          {/* Info cards without photography */}
          {infoCards.map((a, i) => {
            const Icon = INFO_ICONS[i];
            return (
              <FadeUp key={a.t} delay={0.05 * ((i % 3) + 1)}>
                <article
                  data-testid={`attraction-info-${i}`}
                  className="flex h-full flex-col rounded-3xl border border-federasol-terra/10 bg-federasol-cream p-6 shadow-[0_14px_40px_-20px_rgba(64,49,47,0.15)] transition-all duration-500 ease-soft hover:-translate-y-1.5 hover:shadow-[0_24px_50px_-20px_rgba(166,93,71,0.28)] sm:p-7"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-federasol-blush text-federasol-terra">
                    <Icon className="h-5 w-5" strokeWidth={1.6} />
                  </div>
                  <h3 className="mt-5 font-serif text-xl font-semibold text-federasol-cocoa sm:text-2xl">
                    {a.t}
                  </h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-federasol-muted">{a.d}</p>
                </article>
              </FadeUp>
            );
          })}
        </div>
      </div>
    </section>
  );
}
